import { PDFDocument, rgb, StandardFonts, PDFFont, PDFForm, PDFTextField, PDFName, PDFString } from 'pdf-lib'

// Map of template names to their file paths
export const TEMPLATES: Record<string, string> = {
  RETAIL_EV_HYBRID_RELEASE: '/templates/Retail-EV-Hybrid-Release.pdf',
  WATER_DOCTORS_LETTER_HEAD: '/templates/Water-Doctors-Letter-Head.pdf',
  WAIVER_WITH_FIELDS: '/templates/waiver_template_with_fields.pdf',
}

// Data structure for filling templates
export interface VehicleData {
  year?: string
  make?: string
  model?: string
  vin?: string
}

export interface TemplateData {
  date?: string
  customerName?: string
  vehicle?: VehicleData
}

export interface WaiverData {
  renterName?: string
  renterPhone?: string
  renterEmail?: string
  signedByText?: string
  renterInfo?: string
}

/**
 * Utility to safely get a form field by name if it exists
 */
function getFieldMaybe(form: PDFForm, name: string): PDFTextField | null {
  try {
    return form.getTextField(name)
  } catch {
    return null
  }
}

/**
 * Creates a new PDF template programmatically
 */
async function createPdfTemplate(): Promise<PDFDocument> {
  const pdfDoc = await PDFDocument.create()
  const page = pdfDoc.addPage([600, 800])
  const form = pdfDoc.getForm()

  const font: PDFFont = await pdfDoc.embedFont(StandardFonts.Helvetica)
  const fontSize = 12

  // Title
  page.drawText('Vehicle Release Form', {
    x: 50,
    y: 750,
    size: 20,
    font,
    color: rgb(0, 0, 0),
  })

  // Date field
  page.drawText('Date:', { x: 50, y: 700, size: fontSize, font })
  const dateField = form.createTextField('date')
  dateField.setText('')
  dateField.addToPage(page, { x: 100, y: 695, width: 150, height: 20 })

  // Vehicle Info
  page.drawText('Vehicle Information', { x: 50, y: 650, size: 16, font })

  const fields = [
    { name: 'vehicle.year', label: 'Year:', y: 620 },
    { name: 'vehicle.make', label: 'Make:', y: 590 },
    { name: 'vehicle.model', label: 'Model:', y: 560 },
    { name: 'vehicle.vin', label: 'VIN:', y: 530 },
  ]

  fields.forEach(f => {
    page.drawText(f.label, { x: 50, y: f.y, size: fontSize, font })
    const textField = form.createTextField(f.name)
    textField.setText('')
    textField.addToPage(page, { x: 100, y: f.y - 5, width: 200, height: 20 })
  })

  // Customer info
  page.drawText('Customer Information', { x: 50, y: 480, size: 16, font })
  page.drawText('Name:', { x: 50, y: 450, size: fontSize, font })
  const nameField = form.createTextField('customerName')
  nameField.setText('')
  nameField.addToPage(page, { x: 100, y: 445, width: 200, height: 20 })

  return pdfDoc
}

/**
 * Fills a PDF form with provided data
 */
export async function fillPdfTemplate(
  templateName: string | null,
  data: TemplateData
): Promise<Uint8Array> {
  try {
    let pdfDoc: PDFDocument
    let form: PDFForm

    // Load pre-made template
    if (templateName) {
      const response = await fetch(templateName)
      const templateBytes = await response.arrayBuffer()
      pdfDoc = await PDFDocument.load(templateBytes)
      form = pdfDoc.getForm()

      const page = pdfDoc.getPages()[0]

      // Fill date
      if (data.date) {
        const dateField = getFieldMaybe(form, 'date')
        if (dateField) dateField.setText(data.date)
      }

      // Fill customer name
      if (data.customerName) {
        const fieldNames = ['text-field-1', 'text-field-2', 'text-field-3']
        const coords = [
          { x: 150, y: 563 },
          { x: 73, y: 528 },
          { x: 73, y: 465 },
        ]

        fieldNames.forEach((name, idx) => {
          const field = getFieldMaybe(form, name)
          if (field) {
            field.setText(data.customerName)
            const { x, y } = coords[idx]
            page.drawRectangle({
              x,
              y,
              width: 160,
              height: 16,
              color: rgb(1, 1, 0),
              opacity: 0.35,
            })
          }
        })
      }

      // Fill vehicle data
      if (data.vehicle) {
        const vehicleFields: (keyof VehicleData)[] = ['year', 'make', 'model', 'vin']
        vehicleFields.forEach(f => {
          const value = data.vehicle?.[f]
          if (value) {
            const field = getFieldMaybe(form, `vehicle-${f}`)
            if (field) field.setText(value)
          }
        })
      }
    }
    // Create a new template dynamically
    else {
      pdfDoc = await createPdfTemplate()
      form = pdfDoc.getForm()

      if (data.date) form.getTextField('date').setText(data.date)
      if (data.customerName) form.getTextField('customerName').setText(data.customerName)

      if (data.vehicle) {
        const vehicleFields: (keyof VehicleData)[] = ['year', 'make', 'model', 'vin']
        vehicleFields.forEach(f => {
          const value = data.vehicle?.[f]
          if (value) {
            const field = getFieldMaybe(form, `vehicle.${f}`)
            if (field) field.setText(value)
          }
        })
      }
    }

    form.flatten()
    return await pdfDoc.save()
  } catch (error) {
    console.error('Error filling PDF template:', error)
    throw error
  }
}

// Fill waiver_template_with_fields.pdf AcroForm fields + embed signature
export async function generateSignedWaiverWithData(
  signatureDataUrl: string,
  waiverData: WaiverData
): Promise<Blob> {
  const existingPdfBytes = await fetch(TEMPLATES.WAIVER_WITH_FIELDS).then((res) =>
    res.arrayBuffer()
  );

  const pdfDoc = await PDFDocument.load(existingPdfBytes);
  const form = pdfDoc.getForm();
  const timesRoman = await pdfDoc.embedFont(StandardFonts.TimesRoman);

  const renterInfo = waiverData.renterInfo ??
    `Renter's Full Name: ${waiverData.renterName ?? ''}  Renter's Phone Number: ${waiverData.renterPhone ?? ''}  Renter's Email Address: ${waiverData.renterEmail ?? ''}`;

  const fontName = timesRoman.name;
  const da = `/${fontName} 10 Tf 0 g`;

  const setFieldValue = (field: PDFTextField, value: string) => {
    field.acroField.dict.set(PDFName.of('DA'), PDFString.of(da))
    field.acroField.dict.set(PDFName.of('V'), PDFString.of(value))
    field.acroField.dict.delete(PDFName.of('AP'))
  }

  const f1 = getFieldMaybe(form, 'renter_info')
  if (f1) setFieldValue(f1, renterInfo)

  if (waiverData.signedByText) {
    const f2 = getFieldMaybe(form, 'signed_by')
    if (f2) setFieldValue(f2, waiverData.signedByText)
  }

  form.flatten();

  // Embed signature image on the last page
  const signatureImageBytes = await fetch(signatureDataUrl).then((res) =>
    res.arrayBuffer()
  );
  const signatureImage = await pdfDoc.embedPng(signatureImageBytes);

  const pages = pdfDoc.getPages();
  const lastPage = pages[pages.length - 1];
  const { width, height } = lastPage.getSize();

  const boxWidth = 200;
  const boxHeight = 60;
  const imgAspect = signatureImage.width / signatureImage.height;
  const boxAspect = boxWidth / boxHeight;
  let drawWidth: number, drawHeight: number;
  if (imgAspect > boxAspect) {
    drawWidth = boxWidth;
    drawHeight = boxWidth / imgAspect;
  } else {
    drawHeight = boxHeight;
    drawWidth = boxHeight * imgAspect;
  }
  const x = width / 2 - 50 - drawWidth / 2;
  const y = height - 60 - (boxHeight - drawHeight) / 2;
  lastPage.drawImage(signatureImage, { x, y, width: drawWidth, height: drawHeight });

  const pdfBytes = await pdfDoc.save();
  return new Blob([new Uint8Array(pdfBytes)], { type: 'application/pdf' });
}

// Combine waiver PDF + signature
export async function generateSignedWaiver(signatureDataUrl: string) {
  // Fetch base waiver
  const waiverTemplateUrl = "/templates/waiver_template_with_fields.pdf";
  const existingPdfBytes = await fetch(waiverTemplateUrl).then((res) =>
    res.arrayBuffer()
  );

  // Load into pdf-lib
  const pdfDoc = await PDFDocument.load(existingPdfBytes);

  // Embed signature image
  const signatureImageBytes = await fetch(signatureDataUrl).then((res) =>
    res.arrayBuffer()
  );
  const signatureImage = await pdfDoc.embedPng(signatureImageBytes);

  const pages = pdfDoc.getPages();
  const lastPage = pages[pages.length - 1];
  const { width, height } = lastPage.getSize();

  // Define the bounding box
  const boxWidth = 200;
  const boxHeight = 60;

  // Get image’s natural dimensions
  const imgWidth = signatureImage.width;
  const imgHeight = signatureImage.height;
  const imgAspect = imgWidth / imgHeight;
  const boxAspect = boxWidth / boxHeight;

  let drawWidth, drawHeight;

  // Scale to contain (preserve aspect ratio)
  if (imgAspect > boxAspect) {
    // image is wider than box — limit by width
    drawWidth = boxWidth;
    drawHeight = boxWidth / imgAspect;
  } else {
    // image is taller — limit by height
    drawHeight = boxHeight;
    drawWidth = boxHeight * imgAspect;
  }

  // Center inside the box
  const x = width / 2 - 50 - drawWidth / 2;
  const y = height - 60 - (boxHeight - drawHeight) / 2;

  lastPage.drawImage(signatureImage, {
    x,
    y,
    width: drawWidth,
    height: drawHeight,
  });

  const pdfBytes = await pdfDoc.save();
  return new Blob([new Uint8Array(pdfBytes)], { type: "application/pdf" });
}
